import { Box, Grid, LinearProgress } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { CSSProperties } from 'react'
import './Loading.css'

interface LoadingProps {
  style?: CSSProperties
  atCenter?: boolean
  isNotFixed?: boolean
  progress?: number | null
  link?: string
  size?: number
}

/**
 * Loading component to display a loading indicator with optional progress bar.
 *
 * @component
 * @category UI Components
 *
 * @param {object} props - Component properties.
 * @param {object} [props.style] - Additional styles to be applied to the container.
 * @param {boolean} [props.atCenter=false] - If true, centers the loading component within a Grid.
 * @param {boolean} [props.isNotFixed=false] - If true, applies a non-fixed positioning class.
 * @param {number|null} [props.progress=null] - The progress percentage (0-100) for the linear progress bar.
 * @param {string} [props.link] - Custom image link to be displayed instead of the default logo.
 *
 * @returns {JSX.Element} The rendered Loading component.
 */
const Loading: React.FC<LoadingProps> = (props) => {
  const {
    style,
    atCenter = false,
    isNotFixed = false,
    progress = null,
    link,
    size = 104
  } = props

  const env = process.env.REACT_APP_ENVIROMENT ?? ''
  const defaultCircle = env.includes('icce')
    ? '/assets/images/icce-logo-circle.svg'
    : '/assets/images/centre-logo.svg'

  if (atCenter) {
    return (
      <Grid
        container
        alignContent='center'
        alignItems='center'
        sx={{
          minHeight: 'calc(100vh - 72px)'
        }}
      >
        <Loading isNotFixed />
      </Grid>
    )
  }

  return (
    <Box
      className={!isNotFixed ? 'loading' : 'loadingNotFixed'}
      style={style}
      sx={{
        width: size,
        height: size
      }}
    >
      <img
        src={link !== undefined ? link : defaultCircle}
        alt=''
        style={{
          width: `calc(${size}px * 0.48)`,
          height: `calc(${size}px * 0.48)`
        }}
      />
      <CircularProgress
        sx={{
          color: '#098746', // When 'loading.main' is used, in some places on portal the color is not set
          width: `${size}px !important`,
          height: `${size}px !important`
        }}
        className='circular-progress'
        variant='indeterminate'
      />
      {Boolean(progress) && <LinearProgress variant='determinate' value={progress} />}
    </Box>
  )
}

export default Loading
